import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import type {
  ChunkMessage,
  TextChunkMessage,
  SttResultMessage,
  ErrorMessage,
  AvatarConfig,
  DisplayMode
} from '@/entities/realtime-agent/types/websocket-messages';
import type { ChatMessageType } from '@/features/text-chat-with-agent/types/chat-message';
import type { RealtimeProvider } from '@/shared/constants/realtime-providers';
import * as chunkProcessor from '@/entities/realtime-agent/lib/chunk-processor';
import * as messageProcessor from '@/entities/realtime-agent/lib/message-processor';
import { getVoiceChunkQueue } from '@/entities/realtime-agent/model/use-chunk-queue';

/**
 * Realtime Agent 스토어 (단순화됨)
 *
 * 책임:
 * - 순수 상태 관리만 담당
 * - 비즈니스 로직 없음 (순수 함수로 분리됨)
 * - UI 로직 없음 (toast 등은 훅에서 처리)
 */

// 세션 메타데이터 타입 (영상 링크 등) - HEAD에서 가져옴
export interface SessionMetadata {
  links: Array<{ url: string; title?: string }>
  images: Array<{ url: string; title?: string }>
  found: boolean
}

// 채팅 백엔드 타입 (Voice Server vs Backend Realtime)
export type ChatBackendType = 'voice_server' | 'backend_realtime';

// 검색 상태 타입 (entities/backend-realtime의 SearchState와 동일)
import type { SearchState } from '@/entities/backend-realtime';
export type SearchStateType = SearchState;

export interface RealtimeAgentState {
  // WebSocket 연결 상태
  isConnected: boolean;
  sessionId: string | null;
  hasAttemptedConnection: boolean;
  websocket: WebSocket | null; // 호환성 유지

  // 재시도 관련 상태
  isReconnecting: boolean;
  retryCount: number;
  maxRetries: number;
  hasFailedPermanently: boolean;

  // 아바타 설정 상태
  avatarConfig: AvatarConfig | null;
  isAvatarSetupComplete: boolean; // 호환성 유지
  avatarSetupRevision: number; // 재인증/재설정 요청 세대
  agentId: string | null; // 현재 에이전트 ID
  displayMode: DisplayMode; // 웹/키오스크 모드 구분
  realtimeProvider: RealtimeProvider; // Realtime Voice Provider (none, openai, google)
  useRealtimeApi: boolean; // 하위 호환성 (deprecated)

  // 데이터 상태
  chunks: ChunkMessage[]; // 호환성 유지 (Queue Manager와 병행)
  textChunks: TextChunkMessage[]; // 텍스트 전용 (실시간 스트리밍)
  currentSttResult: SttResultMessage | null; // 현재 턴의 STT 결과
  lastError: ErrorMessage | null;
  isInterrupted: boolean;

  // 현재 재생 상태 관리 (UI 상태)
  currentChunk: ChunkMessage | "END" | null; // 현재 재생되어야 하는 청크 (기존 로직 호환)

  // 현재 턴 정보
  currentTurnId: string | null;

  // 로딩 상태 - sendMessage부터 첫 응답까지
  isLoading: boolean;

  // 채팅 메시지 관리
  messages: ChatMessageType[];

  // 세션 메타데이터 (영상 링크 등) - HEAD에서 가져옴
  sessionMetadata: SessionMetadata | null;

  // 오디오 재생 상태
  isAudioPlaying: boolean;

  // 초기화 관련 로딩 상태
  isAvatarLoading: boolean;
  isGreetingLoading: boolean;

  // 동의 상태
  hasConsented: boolean;

  // 서버에서 연결 해제됨 상태 (inactive_warning, disconnect_notice)
  isDisconnectedByServer: boolean;
  disconnectReason: string | null;
  showReconnectModal: boolean;

  // Backend Realtime API 상태 (웹 검색 기능)
  chatBackend: ChatBackendType;
  isSearchEnabled: boolean;
  searchState: SearchStateType;
  searchQuery: string | null;
  backendRealtimeSessionId: string | null;

  // Actions - 순수 setter
  setConnected: (connected: boolean) => void;
  setSessionId: (sessionId: string) => void;
  setHasAttemptedConnection: (attempted: boolean) => void;
  setWebsocket: (websocket: WebSocket | null) => void; // 호환성 유지
  setReconnecting: (reconnecting: boolean) => void;
  setRetryCount: (count: number) => void;
  setHasFailedPermanently: (failed: boolean) => void;
  resetRetryState: () => void;
  setAvatarConfig: (config: AvatarConfig) => void;
  setAvatarSetupComplete: (complete: boolean) => void; // 호환성 유지
  invalidateAvatarSetup: () => void;
  setAgentId: (agentId: string | null) => void; // 현재 에이전트 ID 설정
  setDisplayMode: (mode: DisplayMode) => void; // 웹/키오스크 모드 설정
  setRealtimeProvider: (provider: RealtimeProvider) => void; // Realtime Provider 설정
  setUseRealtimeApi: (useRealtimeApi: boolean) => void; // 하위 호환성 (deprecated)
  setAudioPlaying: (playing: boolean) => void;
  setAvatarLoading: (loading: boolean) => void;
  setGreetingLoading: (loading: boolean) => void;
  setHasConsented: (consented: boolean) => void;
  setDisconnectedByServer: (disconnected: boolean, reason?: string | null) => void;
  setShowReconnectModal: (show: boolean) => void;

  // 청크 관리 (순수 setter)
  setTextChunks: (textChunks: TextChunkMessage[]) => void;

  setSttResult: (sttResult: SttResultMessage | null) => void;
  setError: (error: ErrorMessage) => void;
  setInterrupted: (interrupted: boolean) => void;
  setCurrentTurnId: (turnId: string | null) => void;
  setLoading: (loading: boolean) => void;

  // 재생 상태 관리
  setCurrentChunk: (chunk: ChunkMessage | "END" | null) => void;

  // 메시지 관리 (순수 setter)
  setMessages: (messages: ChatMessageType[]) => void;

  // 세션 메타데이터 액션들 - HEAD에서 가져옴
  setSessionMetadata: (metadata: SessionMetadata | null) => void;
  clearSessionMetadata: () => void;

  // 일괄 초기화
  clearChunks: () => void; // 호환성 유지
  clearTextChunks: () => void;
  clearSttResult: () => void;
  clearCurrentTurn: () => void;
  reset: () => void;

  // 호환성 유지: 메시지 관리 헬퍼
  addUserMessage: (message: string) => void;
  setInitialGreeting: (greetingText: string) => void;

  // 텍스트 채팅용 스트리밍 메시지 관리
  addStreamingAgentMessage: (message: string, turnId: string) => void;
  updateStreamingAgentMessage: (message: string, turnId: string) => void;
  finalizeStreamingMessage: (message: string, turnId: string, contextUsed?: ChatMessageType['context_used']) => void;

  // 호환성 유지: 청크 관리 (내부적으로 Queue Manager 사용)
  addChunk: (chunk: ChunkMessage) => void;
  addTextChunk: (chunk: TextChunkMessage) => void;

  // Backend Realtime API 액션 (웹 검색 기능)
  setChatBackend: (backend: ChatBackendType) => void;
  setSearchEnabled: (enabled: boolean) => void;
  setSearchState: (state: SearchStateType, query?: string | null) => void;
  setBackendRealtimeSessionId: (sessionId: string | null) => void;
}

export const useRealtimeAgentStore = create<RealtimeAgentState>()(
  subscribeWithSelector(
    devtools(
      (set) => ({
        // Initial state
        isConnected: false,
        sessionId: null,
        hasAttemptedConnection: false,
        websocket: null,
        isReconnecting: false,
        retryCount: 0,
        maxRetries: 5,
        hasFailedPermanently: false,
        avatarConfig: null,
        isAvatarSetupComplete: false,
        avatarSetupRevision: 0,
        agentId: null,
        displayMode: 'web',
        realtimeProvider: 'none',
        useRealtimeApi: false,
        chunks: [],
        textChunks: [],
        currentSttResult: null,
        lastError: null,
        isInterrupted: false,
        currentChunk: null,
        currentTurnId: null,
        isLoading: false,
        messages: [],
        sessionMetadata: null, // HEAD에서 가져옴
        isAudioPlaying: false,
        isAvatarLoading: true,
        isGreetingLoading: false,
        hasConsented: false,
        isDisconnectedByServer: false,
        disconnectReason: null,
        showReconnectModal: false,

        // Backend Realtime API 상태 초기값
        chatBackend: 'voice_server',
        isSearchEnabled: false,
        searchState: 'idle',
        searchQuery: null,
        backendRealtimeSessionId: null,

        // Actions - 순수 setter만
        setConnected: (isConnected) =>
          set({ isConnected }, false, 'setConnected'),

        setSessionId: (sessionId) =>
          set({ sessionId }, false, 'setSessionId'),

        setHasAttemptedConnection: (hasAttemptedConnection) =>
          set({ hasAttemptedConnection }, false, 'setHasAttemptedConnection'),

        setWebsocket: (websocket) =>
          set({ websocket }, false, 'setWebsocket'),

        setReconnecting: (isReconnecting) =>
          set({ isReconnecting }, false, 'setReconnecting'),

        setRetryCount: (retryCount) =>
          set({ retryCount }, false, 'setRetryCount'),

        setHasFailedPermanently: (hasFailedPermanently) =>
          set({ hasFailedPermanently }, false, 'setHasFailedPermanently'),

        resetRetryState: () =>
          set({ isReconnecting: false, retryCount: 0, hasFailedPermanently: false }, false, 'resetRetryState'),

        setAvatarConfig: (avatarConfig) =>
          set({ avatarConfig }, false, 'setAvatarConfig'),

        setAvatarSetupComplete: (isAvatarSetupComplete) =>
          set({ isAvatarSetupComplete }, false, 'setAvatarSetupComplete'),

        invalidateAvatarSetup: () =>
          set((state) => ({
            isAvatarSetupComplete: false,
            avatarSetupRevision: state.avatarSetupRevision + 1,
          }), false, 'invalidateAvatarSetup'),

        setAgentId: (agentId) =>
          set({ agentId }, false, 'setAgentId'),

        setDisplayMode: (displayMode) =>
          set({ displayMode }, false, 'setDisplayMode'),

        setRealtimeProvider: (realtimeProvider) =>
          set({
            realtimeProvider,
            useRealtimeApi: realtimeProvider !== 'none',  // 하위 호환성 자동 동기화
          }, false, 'setRealtimeProvider'),

        setUseRealtimeApi: (useRealtimeApi) =>
          set({ useRealtimeApi }, false, 'setUseRealtimeApi'),

        setAudioPlaying: (isAudioPlaying) =>
          set({ isAudioPlaying }, false, 'setAudioPlaying'),

        setAvatarLoading: (isAvatarLoading) =>
          set({ isAvatarLoading }, false, 'setAvatarLoading'),

        setGreetingLoading: (isGreetingLoading) =>
          set({ isGreetingLoading }, false, 'setGreetingLoading'),

        setHasConsented: (hasConsented) =>
          set({ hasConsented }, false, 'setHasConsented'),

        setDisconnectedByServer: (isDisconnectedByServer, disconnectReason = null) =>
          set({ isDisconnectedByServer, disconnectReason }, false, 'setDisconnectedByServer'),

        setShowReconnectModal: (showReconnectModal) =>
          set({ showReconnectModal }, false, 'setShowReconnectModal'),

        // 청크 관리 - 순수 setter
        setTextChunks: (textChunks) =>
          set({ textChunks }, false, 'setTextChunks'),

        setSttResult: (currentSttResult) =>
          set({ currentSttResult }, false, 'setSttResult'),

        setError: (lastError) =>
          // Error settlement is turn-aware in use-agent-messages. A turnless
          // late error must not stop the currently active newer turn.
          set({ lastError }, false, 'setError'),

        setInterrupted: (isInterrupted) =>
          set({ isInterrupted }, false, 'setInterrupted'),

        setCurrentTurnId: (currentTurnId) => {
          set({ currentTurnId }, false, 'setCurrentTurnId');

          // Queue Manager에도 턴 변경 알림
          if (currentTurnId) {
            getVoiceChunkQueue().startNewTurn(currentTurnId);
          }
        },

        setLoading: (isLoading) =>
          set({ isLoading }, false, 'setLoading'),

        // 재생 상태 관리
        setCurrentChunk: (currentChunk) => {
          set({ currentChunk }, false, 'setCurrentChunk');
        },

        // 메시지 관리 - 순수 setter
        setMessages: (messages) =>
          set({ messages }, false, 'setMessages'),

        // 세션 메타데이터 액션들 - HEAD에서 가져옴
        setSessionMetadata: (sessionMetadata) =>
          set({ sessionMetadata }, false, 'setSessionMetadata'),

        clearSessionMetadata: () =>
          set({ sessionMetadata: null }, false, 'clearSessionMetadata'),

        // 일괄 초기화
        clearChunks: () =>
          set({ chunks: [] }, false, 'clearChunks'),

        clearTextChunks: () =>
          set({ textChunks: [] }, false, 'clearTextChunks'),

        clearSttResult: () =>
          set({ currentSttResult: null }, false, 'clearSttResult'),

        clearCurrentTurn: () => {
          set({
            chunks: [],
            textChunks: [],
            currentSttResult: null,
            currentTurnId: null,
            currentChunk: null,
          }, false, 'clearCurrentTurn');

          // Queue Manager도 정리
          getVoiceChunkQueue().clear();
        },

        reset: () => {
          set({
            isConnected: false,
            sessionId: null,
            hasAttemptedConnection: false,
            websocket: null,
            isReconnecting: false,
            retryCount: 0,
            hasFailedPermanently: false,
            avatarConfig: null,
            isAvatarSetupComplete: false,
            avatarSetupRevision: 0,
            agentId: null,
            displayMode: 'web',
            realtimeProvider: 'none',
            useRealtimeApi: false,
            chunks: [],
            textChunks: [],
            currentSttResult: null,
            lastError: null,
            isInterrupted: false,
            currentChunk: null,
            currentTurnId: null,
            isLoading: false,
            messages: [],
            sessionMetadata: null, // HEAD에서 가져옴
            isAudioPlaying: false,
            isAvatarLoading: true,
            isGreetingLoading: false,
            hasConsented: false,
            isDisconnectedByServer: false,
            disconnectReason: null,
            showReconnectModal: false,
            // Backend Realtime API 상태 리셋
            chatBackend: 'voice_server',
            isSearchEnabled: false,
            searchState: 'idle',
            searchQuery: null,
            backendRealtimeSessionId: null,
          }, false, 'reset');

          // Queue Manager도 리셋
          getVoiceChunkQueue().clear();
        },

        // 호환성 유지: 메시지 관리 헬퍼
        addUserMessage: (message) => {
          set((state) => ({
            messages: messageProcessor.addUserMessage(state.messages, message)
          }), false, 'addUserMessage');
        },

        setInitialGreeting: (greetingText) => {
          set((state) => ({
            messages: messageProcessor.setInitialGreeting(state.messages, greetingText)
          }), false, 'setInitialGreeting');
        },

        // 텍스트 채팅용 스트리밍 메시지 관리
        addStreamingAgentMessage: (message, turnId) => {
          set((state) => ({
            messages: messageProcessor.addStreamingAgentMessage(state.messages, message, turnId)
          }), false, 'addStreamingAgentMessage');
        },

        updateStreamingAgentMessage: (message, turnId) => {
          set((state) => ({
            messages: messageProcessor.updateStreamingAgentMessage(state.messages, message, turnId)
          }), false, 'updateStreamingAgentMessage');
        },

        finalizeStreamingMessage: (message, turnId, contextUsed) => {
          set((state) => ({
            messages: messageProcessor.finalizeStreamingMessage(state.messages, message, turnId, contextUsed)
          }), false, 'finalizeStreamingMessage');
        },

        // 호환성 유지: 청크 관리 (Queue Manager로 위임)
        addChunk: (chunk) => {

          const queue = getVoiceChunkQueue();
          const state = useRealtimeAgentStore.getState();

          // 메시지 처리 (채팅 기록용)
          const processResult = chunkProcessor.processVoiceChunk(chunk, {
            chunks: [], // 더 이상 스토어에 쌓지 않음
            messages: state.messages,
            currentTurnId: state.currentTurnId,
          });

          if (processResult) {
            // 메시지만 업데이트
            set({
              messages: processResult.updatedMessages,
              isLoading: false
            }, false, 'addChunk');
          }

          // Queue Manager에 추가 (실제 재생용)
          queue.enqueue(chunk);
        },

        addTextChunk: (textChunk) => {
          set((state) => {

            const processResult = chunkProcessor.processTextChunk(textChunk, {
              textChunks: state.textChunks,
              currentTurnId: state.currentTurnId,
            });

            if (!processResult) {
              return state;
            }

            return {
              textChunks: processResult.textChunks,
              isLoading: processResult.isLoading,
            };
          }, false, 'addTextChunk');
        },

        // Backend Realtime API 액션
        setChatBackend: (chatBackend) =>
          set({ chatBackend }, false, 'setChatBackend'),

        setSearchEnabled: (isSearchEnabled) =>
          set({ isSearchEnabled }, false, 'setSearchEnabled'),

        setSearchState: (searchState, searchQuery = null) =>
          set({ searchState, searchQuery }, false, 'setSearchState'),

        setBackendRealtimeSessionId: (backendRealtimeSessionId) =>
          set({ backendRealtimeSessionId }, false, 'setBackendRealtimeSessionId'),
      }),
      {
        name: 'realtime-agent-store',
      }
    )
  )
);
